import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { catchError, map, mergeMap, of, switchMap } from "rxjs";

import * as companyActions from './company.actions';
import * as companySelectors from './company.selectors';
import * as authSelector from '../../../store/auth/auth.selectors';
import * as operatorActions from '../../../store/operators/operators.actions'

import { CompanyService } from "../services/company.service";
import { CompaniesRequestUtil } from "../utils/companies-request.utils";
import { MessageTranslateService } from "../../../shared/services/message.service";

export class CompanyEffects {
    actions$: Actions = inject(Actions);
    companyService = inject(CompanyService);
    messageService = inject(MessageTranslateService)
    store: Store = inject(Store);

    FailureErrorCode = 500;

    loadCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadCompanyList,
                companyActions.changeCompanyPaginationSuccessful,
                companyActions.insertNewCompanySuccess
            ),
            concatLatestFrom(() => [
                this.store.select(companySelectors.selectCompaniesPagination),
                this.store.select(authSelector.selectUser)
            ]),
            switchMap(([_action, pagination, user]) => {
                const request = CompaniesRequestUtil.createCompanyListRequest(user?.companyId, pagination);
                return this.companyService.getCompaniesPage(request)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadCompanyListSuccess({ companiesResponse: res.body!, pageIndex: request.pageIndex!, pageSize: request.pageSize! });
                            } else {
                                return companyActions.loadCompanyListFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(
                                companyActions.loadCompanyListFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading companies failed' })
                            )
                        )
                    )
                }
            )
        )
    );

    loadCompaniesCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadCompanyCount,
                companyActions.insertNewCompanySuccess
            ),
            concatLatestFrom(() => [
                this.store.select(authSelector.selectUser),
            ]),
            switchMap(([_action, user]) => {
                const request = CompaniesRequestUtil.createCompanyCountRequest(user?.companyId);
                return this.companyService.getCompaniesCount(request)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadCompanyCountSuccess({ countResponse: res.body! });
                            } else {
                                return companyActions.loadCompanyCountFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(
                                companyActions.loadCompanyCountFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading company count failed' })
                            )
                        )
                    )
                }
            )
        )
    );

    changedCompaniesPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(companyActions.changeCompanyPagination),
            map((_) => companyActions.changeCompanyPaginationSuccessful())
        )
    );

    insertNewCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.insertNewCompany
            ),
            switchMap((requestData) =>
                this.companyService.createCompany(
                    requestData.createCompanyRequest
                ).pipe(
                    map((res) => {
                        if (res.ok) {
                            return companyActions.insertNewCompanySuccess({ result: res.body! })
                        }
                        return companyActions.insertNewCompanyFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                    }),
                    catchError((err) =>
                        of(
                            companyActions.insertNewCompanyFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Inserting company failed' })
                        )
                    )
                )
            )
        )
    );

    loadOperatorsAfterCompanyInsert$ = createEffect(() =>
        this.actions$.pipe(
          ofType(companyActions.insertNewCompanySuccess, companyActions.updateCompanySuccess),
          map(() => operatorActions.LoadOperators())
        )
    );

    updateCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.updateCompany
            ),
            switchMap((requestData) =>
                this.companyService.updateCompany(
                    requestData.companyId, requestData.updateCompanyRequest
                ).pipe(
                    mergeMap((res) => {
                        if (res.ok) {
                            return of(
                                companyActions.updateCompanySuccess({ result: res.body! }),
                                companyActions.loadCompanyOverview({ companyId: requestData.companyId })
                            );
                        }
                        return of(companyActions.updateCompanyFailure({ errorCode: res.status!, errorMessage: res.statusText! }));
                    }),
                    catchError((err) =>
                        of(
                            companyActions.updateCompanyFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Updating company failed' })
                        )
                    )
                )
            )
        )
    );

    loadCompanyOverview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadCompanyOverview,
            ),
            switchMap((requestData) =>
                this.companyService.getCompanyOverview(requestData.companyId)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadCompanyOverviewSuccess({ result: res.body! })
                            } else {
                                return companyActions.loadCompanyOverviewFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(companyActions.loadCompanyOverviewFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading company overview failed' }))
                        )
                    )
            )
        )
    );

    loadCompanyUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadUserList,
                companyActions.changeUserPaginationSuccessful,
                companyActions.insertNewCompanyUserSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(companySelectors.selectCompanyOverview),
                this.store.select(companySelectors.selectCompanyUsersPagination),
            ]),
            switchMap(([_action, company, pagination]) => {
                const request = CompaniesRequestUtil.createCompanyUsersListRequest(company?.id, pagination);
                return this.companyService.getCompanyUsersPage(request)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadUserListSuccess({ usersResponse: res.body!, pageIndex: request.pageIndex!, pageSize: request.pageSize! });
                            } else {
                                return companyActions.loadUserListFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(
                                companyActions.loadUserListFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading company users failed' })
                            )
                        )
                    )
                }
            )
        )
    );

    loadCompanyUsersCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadUserCount,
                companyActions.insertNewCompanyUserSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(companySelectors.selectCompanyOverview),
            ]),
            switchMap(([_action, company]) => {
                const request = CompaniesRequestUtil.createCompanyUsersListRequest(company?.id);
                return this.companyService.getCompanyUsersCount(request)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadUserCountSuccess({ countResponse: res.body! });
                            } else {
                                return companyActions.loadUserCountFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(
                                companyActions.loadUserCountFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading company users count failed' })
                            )
                        )
                    )
                }
            )
        )
    );

    changedCompanyUsersPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(companyActions.changeUserPagination),
            map((_) => companyActions.changeUserPaginationSuccessful())
        )
    );

    insertNewUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.insertNewCompanyUser
            ),
            switchMap((requestData) =>
                this.companyService.createCompanyUser(
                    requestData.createCompanyUserRequest
                ).pipe(
                    map((res) => {
                        if (res.ok) {
                            return companyActions.insertNewCompanyUserSuccess({ result: res.body! })
                        }
                        return companyActions.insertNewCompanyUserFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                    }),
                    catchError((err) =>
                        of(
                            companyActions.insertNewCompanyUserFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Inserting company user failed' })
                        )
                    )
                )
            )
        )
    );

    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(companyActions.deleteUser),
            concatLatestFrom(() => [
                this.store.select(companySelectors.selectCompanyOverview)
            ]),
            switchMap(([requestData, companyOverview]) =>
                this.companyService.deleteCompanyUser(requestData.user.email!)
                    .pipe(
                        mergeMap((res) => {
                            if (res.ok) {
                                return of(
                                    companyActions.deleteUserSuccess({ userId: requestData.user.id! }),
                                    companyActions.loadUserCount({ companyId: companyOverview!.id! }),
                                    companyActions.loadUserList({ companyId: companyOverview!.id! })
                                )
                            }
                            return of(companyActions.deleteUserFailure({ errorCode: res.status!, errorMessage: res.statusText! }));
                        })
                    )
            )
        )
    );

    updateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(companyActions.updateUser),
            concatLatestFrom(() => [
                this.store.select(companySelectors.selectCompanyOverview)
            ]),
            switchMap(([requestData, companyOverview]) =>
                this.companyService.updateCompanyUser(requestData?.updateCompanyUserRequest, requestData.password)
                    .pipe(
                        mergeMap((res) => {
                            if (res.ok) {
                                return of(
                                    companyActions.updateUserSuccess({ result: requestData.updateCompanyUserRequest! }),
                                    companyActions.loadUserCount({ companyId: companyOverview!.id! }),
                                    companyActions.loadUserList({ companyId: companyOverview!.id! }),
                                    companyActions.loadCompanyUserOverview({ companyUserId: requestData.updateCompanyUserRequest?.id })
                                )
                            }
                            return of(companyActions.updateUserFailure({ errorCode: res.status!, errorMessage: res.statusText! }));
                        })
                    )
            )
        )
    );

    loadRoles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadAuthRoles
            ),
            switchMap((requestData) => {
                return this.companyService.getAuthRoles(requestData.roleIds)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadAuthRolesSuccess({ rolesResponse: res.body! });
                            } else {
                                return companyActions.loadAuthRolesFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(
                                companyActions.loadAuthRolesFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading auth roles failed' })
                            )
                        )
                    )
                }
            )
        )
    );

    loadCompanyUserOverview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.loadCompanyUserOverview,
            ),
            switchMap((requestData) =>
                this.companyService.getCompanyUserOverview(requestData.companyUserId)
                    .pipe(
                        map((res) => {
                            if (res.ok) {
                                return companyActions.loadCompanyUserOverviewSuccess({ result: res.body! })
                            } else {
                                return companyActions.loadCompanyUserOverviewFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                            }
                        }),
                        catchError((err) =>
                            of(companyActions.loadCompanyUserOverviewFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Loading company user overview failed' }))
                        )
                    )
            )
        )
    );

    inviteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.inviteUser
            ),
            switchMap((requestData) =>
                this.companyService.inviteUser(
                    requestData.email
                ).pipe(
                    map((res) => {
                        if (res.ok) {
                            return companyActions.inviteUserSuccess()
                        }
                        return companyActions.inviteUserFailure({ errorCode: res.status!, errorMessage: res.statusText! })
                    }),
                    catchError((err) =>
                        of(
                            companyActions.inviteUserFailure({ errorCode: this.FailureErrorCode,  errorMessage: err.message || 'Inviting company user failed' })
                        )
                    )
                )
            )
        )
    );

    showSuccessNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.insertNewCompanySuccess,
                companyActions.updateCompanySuccess,
                companyActions.deleteUserSuccess,
                companyActions.updateUserSuccess,
                companyActions.insertNewCompanyUserSuccess,
                companyActions.inviteUserSuccess
            ),
            map((action) => {
                if (action.type == companyActions.insertNewCompanySuccess.type || action.type == companyActions.updateCompanySuccess.type) {
                    this.messageService.successTranslate("companies.insert.save-success");
                } else if (action.type == companyActions.deleteUserSuccess.type) {
                    this.messageService.successTranslate("companies.overview.users.delete-success");
                } else if (action.type == companyActions.updateUserSuccess.type || action.type == companyActions.insertNewCompanyUserSuccess.type) {
                    this.messageService.successTranslate("companies.overview.users.save-success");
                } else if (action.type == companyActions.inviteUserSuccess.type) {
                    this.messageService.successTranslate("companies.overview.users.speed-dial.reinvite-successful");
                }
            })
        ),
        { dispatch: false }
    );

    showErrorNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                companyActions.insertNewCompanyFailure,
                companyActions.updateCompanyFailure,
                companyActions.deleteUserFailure,
                companyActions.updateUserFailure,
                companyActions.insertNewCompanyUserFailure,
                companyActions.inviteUserFailure
            ),
            map((action) => {
                if (action.type == companyActions.insertNewCompanyFailure.type || action.type == companyActions.updateCompanyFailure.type) {
                    this.messageService.errorTranslate("companies.insert.save-error");
                } else if (action.type == companyActions.deleteUserFailure.type) {
                    this.messageService.errorTranslate("companies.overview.users.delete-error");
                } else if (action.type == companyActions.updateUserFailure.type || action.type == companyActions.insertNewCompanyUserFailure.type) {
                    this.messageService.errorTranslate("companies.overview.users.save-error");
                } else if (action.type == companyActions.inviteUserFailure.type) {
                    this.messageService.errorTranslate("companies.overview.users.speed-dial.reinvite-unsuccessful");
                }
            })
        ),
        { dispatch: false }
    )
}